/**
 * Gateway WebSocket Client for LexIDE.
 *
 * Connects to the per-user OpenClaw runtime via WebSocket.
 * Handles: auth handshake, RPC request/response, EventFrame streaming,
 * auto-reconnect with exponential backoff.
 *
 * Protocol: OpenClaw gateway protocol v1 (JSON frames).
 * See: lexruntime/openclaw/src/gateway/protocol/
 */

/* ---------- Frame types (match gateway protocol) ---------- */

interface RequestFrame {
  type: "req";
  id: string;
  method: string;
  params: Record<string, unknown>;
}

/**
 * A rejected gateway RPC, carrying the server's error CODE.
 *
 * The wire frame has always included `error.code`, but the reject path
 * discarded it and kept only the message — so a caller could not branch
 * on the failure kind without matching prose. That is unworkable for
 * methods whose codes mean different things to the user: the recycle
 * bin, for instance, must tell CONFLICT ("a live case occupies that
 * folder — rename it first") apart from NO_RECEIPT ("not auto-restorable,
 * do not offer a retry"), and the messages are localized pt-BR.
 *
 * Extends `Error`, so every existing `catch (e) { e.message }` caller is
 * unaffected; `code` is simply available to those that want it.
 */
export class GatewayRpcError extends Error {
  readonly code?: string;
  readonly details?: unknown;
  constructor(message: string, code?: string, details?: unknown) {
    super(message);
    this.name = "GatewayRpcError";
    this.code = code;
    this.details = details;
  }
}

/**
 * Should a FAILED heartbeat tear the socket down?
 *
 * Only SILENCE means death. An error RESPONSE (`GatewayRpcError`) proves the
 * server received the frame and answered, so the pipe is demonstrably alive —
 * closing on it is how a missing `ping` method became a permanent reconnect
 * storm (lexruntime handback 2026-07-22 §1). Exported so the rule is unit-tested
 * rather than buried in a private method.
 */
export function heartbeatFailureIsFatal(err: unknown): boolean {
  return !(err instanceof GatewayRpcError);
}

interface ResponseFrame {
  type: "res";
  id: string;
  ok: boolean;
  payload?: unknown;
  error?: { code: string; message: string; details?: unknown };
}

interface EventFrame {
  type: "event";
  event: string;
  seq?: number;
  payload: unknown;
}

interface ConnectChallenge {
  type: "event";
  event: "connect.challenge";
  payload: { nonce: string; ts: number };
}

interface HelloOk {
  type: "hello-ok";
  protocol: number;
  server: { version: string; connId: string };
  features: { methods: string[]; events: string[] };
  auth?: { deviceToken?: string; role?: string; scopes?: string[] };
  policy?: { maxPayload?: number; tickIntervalMs?: number };
}

import { isAuthClose, isTerminalAuthFailure } from "@/lib/auth-close";

/* ---------- Client types ---------- */

/**
 * ⚠ `unauthorized` is TERMINAL: the reconnect scheduler must never arm from it.
 * It means the credential itself was refused repeatedly (see `lib/auth-close.ts`
 * for why repetition, not a single 1008, is the evidence). Every other member
 * describes a transport condition that can recover on its own.
 */
export type ConnectionState = "disconnected" | "connecting" | "authenticating" | "connected" | "reconnecting" | "error" | "unauthorized";

export interface GatewayClientOptions {
  url: string;
  token: string;
  clientId?: string;
  clientMode?: string;
  requestTimeoutMs?: number;
  /** Liveness-probe cadence while connected. */
  heartbeatIntervalMs?: number;
  /** Deadline for a single beat — short by design; see `probeLiveness`. */
  heartbeatTimeoutMs?: number;
  maxReconnectDelayMs?: number;
  /**
   * WebSocket subprotocols offered to the server in the upgrade
   * request (`Sec-WebSocket-Protocol` header). Used by lexserver's
   * runtime-WS proxy in BOTH dev and prod (post-2026-05-31 retirement
   * of the legacy generic dev container): the SPA opens
   *   new WebSocket(url, [`lexiun-jwt.${token}`])
   * so the proxy can verify the JWT and resolve the per-user runtime
   * BEFORE the upgrade is accepted. Pre-2026-05-31 dev left this unset
   * because the Vite proxy forwarded directly to the generic
   * `lexruntime:18789` container which had no per-user routing
   * decision; that path is retired.
   *
   * When unset (legacy break-glass), the WebSocket is opened with no
   * subprotocol — same as the pre-2026-05-09 behavior; the lexserver
   * proxy will reject with 401 since it has no JWT to route by.
   */
  subprotocols?: readonly string[];
  /**
   * Re-fetch auth before each reconnect. Called from `scheduleReconnect`
   * and from `forceReconnect` (visibility-change wake-up). Must return
   * a fresh in-band `token` AND fresh `subprotocols` because the
   * routing-token JWT inside the subprotocol expires (~1h) and the
   * in-band gateway-token may rotate. Without this hook a tab left
   * open overnight reconnects forever with a stale JWT and never
   * recovers until the user F5-reloads. Optional — clients that
   * don't have refreshable creds (legacy "lexdev" dev path) can omit.
   *
   * Best-effort: if it throws, the reconnect attempt uses the
   * previously-cached values and may also fail — the next reconnect
   * tries again. NEVER blocks the reconnect loop indefinitely.
   */
  refreshAuth?: () => Promise<{ token: string; subprotocols?: readonly string[] }>;
  onStateChange?: (state: ConnectionState) => void;
  onEvent?: (event: string, payload: unknown) => void;
  onError?: (error: Error) => void;
  /**
   * Maintenance/recovery signal. Fires `true` when the socket closed
   * with code **1008** (policy/auth — the class a planned per-user
   * runtime recreate produces: the gateway rejects the in-band token
   * while it rotates, or the connect times out mid-redeploy) and the
   * client is auto-recovering; fires `false` once a connection is
   * re-established. Lets the UI render a CALM "workspace updating"
   * surface and suppress alarming error banners/toasts during the
   * brief auto-recovering window, instead of the scary disconnect
   * banner. Distinct from `onStateChange("reconnecting")` which also
   * covers ordinary network blips. See
   * `ai-docs/gateway-token-convergence-plan.md` §Layer 2.
   */
  onMaintenance?: (active: boolean) => void;
}

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/* ---------- GatewayClient ---------- */

export class GatewayClient {
  private ws: WebSocket | null = null;
  private heartbeatTimer: number | null = null;
  /** One-shot guard so a runtime without `ping` warns once, not every 20 s. */
  private warnedHeartbeatUnsupported = false;
  private state: ConnectionState = "disconnected";
  private opts: Required<Pick<GatewayClientOptions, "url" | "token" | "clientId" | "clientMode" | "requestTimeoutMs" | "maxReconnectDelayMs">> & GatewayClientOptions;
  private pending = new Map<string, PendingRequest>();
  private backoffMs = 1000;
  /** True once a 1008 (policy/auth) close put us into calm
   *  auto-recovery; cleared on the next successful connect. Prevents
   *  re-emitting `onMaintenance(true)` on every backoff tick. */
  private inMaintenance = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Consecutive auth-class closes. Reset on any successful connect — that
   *  reset is what lets a runtime rotation recover instead of being declared
   *  dead. See `lib/auth-close.ts`. */
  private consecutiveAuthCloses = 0;
  private closed = false;
  private lastSeq: number | null = null;
  private reqCounter = 0;

  constructor(options: GatewayClientOptions) {
    this.opts = {
      clientId: "openclaw-control-ui",
      clientMode: "ui",
      requestTimeoutMs: 30_000,
      heartbeatIntervalMs: 20_000,
      heartbeatTimeoutMs: 8_000,
      maxReconnectDelayMs: 30_000,
      ...options,
    };
  }

  /** Current connection state. */
  getState(): ConnectionState {
    return this.state;
  }

  /** Connect to the gateway. */
  connect(): void {
    if (this.closed) return;
    this.setState("connecting");
    this.doConnect();
  }

  /** Disconnect and stop reconnecting. */
  disconnect(): void {
    this.closed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close(1000, "client disconnect");
      this.ws = null;
    }
    this.rejectAllPending("Client disconnected");
    this.setState("disconnected");
  }

  /** Update the auth token (e.g., after refresh). */
  setToken(token: string): void {
    this.opts.token = token;
  }

  /** Send an RPC request and wait for the response. `timeoutMs` overrides
   *  the client default for known-slow calls (e.g. `lx.stt_transcribe`,
   *  whose cold whisper-model load can exceed the 30s default). */
  async request<T = unknown>(
    method: string,
    params: Record<string, unknown> = {},
    timeoutMs?: number,
  ): Promise<T> {
    if (this.state !== "connected" || !this.ws) {
      throw new Error(`Gateway not connected (state: ${this.state})`);
    }

    const id = `req-${++this.reqCounter}-${Date.now().toString(36)}`;
    const frame: RequestFrame = { type: "req", id, method, params };
    const effectiveTimeout = timeoutMs ?? this.opts.requestTimeoutMs;

    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`Request timeout: ${method} (${effectiveTimeout}ms)`));
      }, effectiveTimeout);

      this.pending.set(id, {
        resolve: resolve as (v: unknown) => void,
        reject,
        timer,
      });

      this.ws!.send(JSON.stringify(frame));
    });
  }

  /* ---------- Internal ---------- */

  private doConnect(): void {
    // Guard against double-connect
    if (this.ws) {
      this.ws.close(1000, "replacing connection");
      this.ws = null;
    }

    try {
      // Pass subprotocols only when set — local dev keeps its
      // proxy-direct path, production hands the JWT to the lexserver
      // WS proxy via `lexiun-jwt.<token>`.
      const subs = this.opts.subprotocols;
      this.ws = subs && subs.length > 0
        ? new WebSocket(this.opts.url, subs as string[])
        : new WebSocket(this.opts.url);
    } catch (err) {
      this.handleConnectError(err instanceof Error ? err : new Error(String(err)));
      return;
    }

    this.ws.onopen = () => {
      if (this.closed) return;
      this.setState("authenticating");
      // Wait for connect.challenge from server, then send connect params
    };

    this.ws.onmessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? ev.data : String(ev.data);
        const frame = JSON.parse(data) as ResponseFrame | EventFrame | HelloOk | ConnectChallenge;
        this.handleFrame(frame);
      } catch (err) {
        this.opts.onError?.(err instanceof Error ? err : new Error("Failed to parse gateway frame"));
      }
    };

    this.ws.onerror = () => {
      // onerror fires before onclose; handle in onclose
    };

    this.ws.onclose = (ev) => {
      this.stopHeartbeat();
      this.ws = null;
      this.rejectAllPending(`Connection closed: ${ev.code} ${ev.reason}`);

      if (!this.closed) {
        // 1008 = policy/auth close — the class a planned per-user
        // runtime recreate produces (token rejected while rotating, or
        // a connect-timeout mid-redeploy). Enter calm maintenance mode
        // so the UI shows "workspace updating", not the alarming
        // disconnect banner. Emitted once (idempotent across backoff
        // ticks); cleared on the next successful connect.
        // ⚖ Auth-class close: count it, and only declare the session dead once
        // the evidence is in. A single 1008 is the ordinary rotation signal.
        if (isAuthClose(ev.code)) {
          const terminal = isTerminalAuthFailure({
            code: ev.code,
            priorConsecutiveAuthCloses: this.consecutiveAuthCloses,
          });
          this.consecutiveAuthCloses += 1;
          if (terminal) {
            // ⚠ TERMINAL — do NOT scheduleReconnect(). Retrying a refused
            // credential spends real requests on a guaranteed rejection and
            // renders as "reconnecting", which promises a recovery that cannot
            // happen. Leaving maintenance mode too: this is not an update.
            if (this.inMaintenance) {
              this.inMaintenance = false;
              this.opts.onMaintenance?.(false);
            }
            this.setState("unauthorized");
            return;
          }
          if (!this.inMaintenance) {
            this.inMaintenance = true;
            this.opts.onMaintenance?.(true);
          }
        } else {
          // ⚠ ANY non-auth close resets the counter. A flapping network (1006)
          // interleaved with a rotation must not accumulate toward "dead
          // session" — that is the S4 regression in counter form.
          this.consecutiveAuthCloses = 0;
        }
        this.scheduleReconnect();
      } else {
        this.setState("disconnected");
      }
    };
  }

  private handleFrame(frame: ResponseFrame | EventFrame | HelloOk | ConnectChallenge): void {
    // Connect challenge → send connect params
    if ("event" in frame && frame.event === "connect.challenge") {
      this.sendConnectParams();
      return;
    }

    // Response to a pending RPC request (including connect handshake)
    if ("type" in frame && (frame as { type: string }).type === "res") {
      const res = frame as ResponseFrame;
      const p = this.pending.get(res.id);
      if (p) {
        this.pending.delete(res.id);
        clearTimeout(p.timer);
        if (res.ok) {
          // Check if this is the connect response (hello-ok inside payload)
          const payload = res.payload as Record<string, unknown> | undefined;
          if (payload && payload.type === "hello-ok") {
            this.backoffMs = 1000;
            // Recovered — exit calm maintenance mode if we were in it.
            if (this.inMaintenance) {
              this.inMaintenance = false;
              this.opts.onMaintenance?.(false);
            }
            // ⚖ The reset that makes a rotation RECOVERABLE: one successful
            // connect wipes the auth-close history, so only an UNBROKEN run of
            // refusals can reach the terminal threshold.
            this.consecutiveAuthCloses = 0;
            this.setState("connected");
            this.startHeartbeat();
          }
          p.resolve(res.payload);
        } else {
          p.reject(
            new GatewayRpcError(
              res.error?.message ?? "RPC error",
              res.error?.code,
              res.error?.details,
            ),
          );
        }
      }
      return;
    }

    // EventFrame → dispatch to subscriber
    if ("type" in frame && (frame as { type: string }).type === "event") {
      const evt = frame as EventFrame;
      if (typeof evt.seq === "number") {
        if (this.lastSeq !== null && evt.seq !== this.lastSeq + 1) {
          // Gap detected — could request replay
        }
        this.lastSeq = evt.seq;
      }
      this.opts.onEvent?.(evt.event, evt.payload);
      return;
    }
  }

  private sendConnectParams(): void {
    if (this.closed || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    const connectParams = {
      minProtocol: 3,
      maxProtocol: 3,
      client: {
        id: this.opts.clientId,
        displayName: "LexIDE",
        version: "1.0.0",
        platform: "web",
        mode: this.opts.clientMode,
      },
      auth: {
        token: this.opts.token,
      },
      role: "operator",
      scopes: ["operator.admin"],
    };

    // ConnectParams must be sent as a RequestFrame with method "connect"
    const id = `req-connect-${Date.now().toString(36)}`;
    const frame: RequestFrame = { type: "req", id, method: "connect", params: connectParams as unknown as Record<string, unknown> };

    // Register a pending handler for the hello-ok response
    const timer = setTimeout(() => {
      this.pending.delete(id);
      this.opts.onError?.(new Error("Connect handshake timeout"));
      this.ws?.close(1008, "connect timeout");
    }, this.opts.requestTimeoutMs);

    this.pending.set(id, {
      resolve: () => {
        // hello-ok is handled in handleFrame, not here
      },
      reject: (err) => {
        this.opts.onError?.(err);
      },
      timer,
    });

    this.ws.send(JSON.stringify(frame));
  }

  private scheduleReconnect(): void {
    this.setState("reconnecting");
    const delay = Math.min(this.backoffMs, this.opts.maxReconnectDelayMs);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.closed) return;
      this.backoffMs = Math.min(this.backoffMs * 2, this.opts.maxReconnectDelayMs);
      void this.reconnectWithRefresh();
    }, delay);
  }

  /**
   * Resets backoff and immediately attempts a fresh-auth reconnect.
   * Called by `useGateway`'s visibilitychange listener when the tab
   * comes back from hidden — bypasses the up-to-30s backoff so the
   * user doesn't stare at "Reconnecting..." while we wait for the
   * next backoff tick.
   *
   * Cancels any pending reconnect timer + closes any half-open ws.
   * NEVER throws — auth-refresh failure is reported via onError and
   * the regular reconnect loop continues.
   */
  forceReconnect(): void {
    if (this.closed) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try { this.ws.close(1000, "force reconnect"); } catch { /* noop */ }
      this.ws = null;
    }
    this.backoffMs = 1000; // reset to initial
    this.setState("reconnecting");
    void this.reconnectWithRefresh();
  }

  /** Refresh auth (if a callback is configured) then doConnect. */
  private async reconnectWithRefresh(): Promise<void> {
    if (this.closed) return;
    if (this.opts.refreshAuth) {
      try {
        const refreshed = await this.opts.refreshAuth();
        this.opts.token = refreshed.token;
        if (refreshed.subprotocols !== undefined) {
          this.opts.subprotocols = refreshed.subprotocols;
        }
      } catch (err) {
        // Best-effort: log but proceed with cached creds. Next
        // reconnect tick tries again.
        this.opts.onError?.(
          err instanceof Error
            ? new Error(`refreshAuth failed (reconnect with cached creds): ${err.message}`)
            : new Error("refreshAuth failed"),
        );
      }
    }
    if (this.closed) return;
    this.doConnect();
  }

  private handleConnectError(err: Error): void {
    this.opts.onError?.(err);
    if (this.closed) {
      this.setState("error");
    } else {
      this.scheduleReconnect();
    }
  }

  private rejectAllPending(reason: string): void {
    for (const [id, p] of this.pending) {
      clearTimeout(p.timer);
      p.reject(new Error(reason));
      this.pending.delete(id);
    }
  }

  /**
   * LIVENESS PROBE — detects a socket that died WITHOUT a close event.
   *
   * `onclose` covers an orderly drop, but a per-user runtime removed with
   * `docker rm -f` (every fleet roll does this) can leave the socket
   * half-open: no FIN, no `onclose`, so the client still believes it is
   * `connected`. Every subsequent request then burns its full 30 s timeout
   * and the UI reports "the backend is hanging" when the truth is "your
   * socket is dead". This cost a cross-lane bug report once — the
   * misdiagnosis is expensive because a timeout looks identical whether the
   * handler stalled or the pipe is gone.
   *
   * So: a cheap `ping` on an interval with a SHORT deadline. A missed beat
   * closes the socket, which routes into the existing `onclose` →
   * `scheduleReconnect()` path rather than adding a second recovery
   * mechanism. `__rpc` liveness follows for free, because the store drops
   * the handle on the resulting state change.
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      void this.probeLiveness();
    }, this.opts.heartbeatIntervalMs) as unknown as number;
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer as unknown as ReturnType<typeof setInterval>);
      this.heartbeatTimer = null;
    }
  }

  /** One beat. Deliberately uses the SHORT heartbeat deadline, not the
   *  request timeout — the point is to notice death in seconds, not after
   *  the 30 s a real call would take. */
  private async probeLiveness(): Promise<void> {
    if (this.state !== "connected" || !this.ws) return;
    try {
      await this.request("ping", {}, this.opts.heartbeatTimeoutMs);
    } catch (err) {
      // An ERROR RESPONSE proves the pipe is ALIVE — the server received the
      // frame and answered. Only SILENCE (a timeout) means death.
      //
      // Conflating the two was half of a real outage (lexruntime handback
      // 2026-07-22 §1): the runtime had no `ping` method, so every beat came
      // back `unknown method: ping` — an error response — and this catch closed
      // the socket on each one. The result was a permanent reconnect storm on
      // every tab, which surfaced as "long requests drop the connection"
      // because a call only survived if it fit inside the remaining slice of
      // the ~20 s window. The runtime registered `ping`, but the client must
      // not be able to kill a demonstrably-live connection again.
      if (!heartbeatFailureIsFatal(err)) {
        if (!this.warnedHeartbeatUnsupported) {
          this.warnedHeartbeatUnsupported = true;
          const reason = err instanceof Error ? err.message : String(err);
          console.warn(
            `[gateway] heartbeat: server answered "ping" with an error (${reason}). ` +
              `Connection is alive (it responded), so it is NOT being closed — but ` +
              `liveness is no longer actively verified until "ping" is available.`,
          );
        }
        return;
      }
      // Missed beat ⇒ treat the pipe as gone. Closing routes into onclose,
      // which rejects everything pending and schedules the reconnect; we do
      // not duplicate that logic here.
      if (this.state === "connected" && this.ws) {
        try {
          this.ws.close(4000, "heartbeat timeout");
        } catch {
          // Already tearing down — onclose will still run.
        }
      }
    }
  }

  private setState(state: ConnectionState): void {
    if (this.state !== state) {
      this.state = state;
      this.opts.onStateChange?.(state);
    }
  }
}
